import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Archive, ArrowLeft, Pencil, Plus, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, extractApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { canArchiveCRM, canCreateOrEditCRM, canDeleteActivity, canRestoreCRM } from "../lib/crm-permissions";
import {
  ASSIGNEE_UNASSIGNED,
  getAssigneeLabel,
  getTaskAssigneeLabel,
  toAssignedUserId,
} from "../lib/tasks-assignees";
import { isTaskOverdue } from "../lib/task-detail";
import PageHeader from "../components/layout/PageHeader";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import StatusPill from "../components/status/StatusPill";
import Timeline from "../components/activity/Timeline";
import AddActivityDialog from "../components/activity/AddActivityDialog";
import QuickNoteDialog from "../components/activity/QuickNoteDialog";
import DealFormDialog from "../components/deals/DealFormDialog";
import { formatDate, formatMoney } from "../lib/format";
import { PRIORITY, TASK_STATUS } from "../lib/enums";
import { DEAL_STAGE_LABELS, PRIORITY_LABELS, TASK_STATUS_LABELS } from "../lib/labels";
import {
  DETAIL_ACTIVITY_PAGE_SIZE,
  mergeActivities,
  normalizeActivityPageResponse,
} from "../lib/activity-pagination";

const TASK_FORM_INITIAL = {
  title: "",
  description: "",
  due_date: "",
  priority: PRIORITY.MEDIUM,
  status: TASK_STATUS.OPEN,
  assigned_user_id: ASSIGNEE_UNASSIGNED,
};

function billingModelLabel(value) {
  if (value === "hybrid") return "Hybrid";
  if (value === "recurring") return "Wiederkehrend";
  return "Einmalig";
}

export default function DealDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role, isSuperAdmin } = useAuth();
  const perm = { role, isSuperAdmin };
  const canDeleteActivities = canDeleteActivity(perm);

  const [deal, setDeal] = useState(null);
  const [company, setCompany] = useState(null);
  const [contact, setContact] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activitiesHasMore, setActivitiesHasMore] = useState(false);
  const [activitiesCursor, setActivitiesCursor] = useState(null);
  const [activitiesLoadingMore, setActivitiesLoadingMore] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [assigneesLoading, setAssigneesLoading] = useState(true);
  const [assigneesError, setAssigneesError] = useState("");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState(TASK_FORM_INITIAL);
  const [taskSaving, setTaskSaving] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [quickNoteDialogOpen, setQuickNoteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const assigneesById = useMemo(() => {
    const map = {};
    for (const user of assignees || []) {
      if (user?.id) map[user.id] = user;
    }
    return map;
  }, [assignees]);

  const assigneeOptions = useMemo(() => {
    const items = (assignees || [])
      .filter((user) => user?.id)
      .map((user) => ({ id: user.id, label: getAssigneeLabel(user) }));
    items.sort((a, b) => a.label.localeCompare(b.label, "de"));
    return items;
  }, [assignees]);

  const loadAssignees = useCallback(async () => {
    setAssigneesLoading(true);
    setAssigneesError("");
    try {
      const response = await api.get("/api/settings/users");
      setAssignees(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setAssignees([]);
      setAssigneesError(extractApiError(error, "Teammitglieder konnten nicht geladen werden."));
      toast.error(extractApiError(error, "Teammitglieder konnten nicht geladen werden."));
    } finally {
      setAssigneesLoading(false);
    }
  }, []);

  const loadTasks = useCallback(async () => {
    try {
      const response = await api.get("/api/tasks", { params: { related_deal_id: id } });
      setTasks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setTasks([]);
      toast.error(extractApiError(error, "Aufgaben konnten nicht geladen werden."));
    }
  }, [id]);

  const loadActivities = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      if (append) setActivitiesLoadingMore(true);
      try {
        const params = { deal_id: id, limit: DETAIL_ACTIVITY_PAGE_SIZE };
        if (cursor?.created_at && cursor?.id) {
          params.before_created_at = cursor.created_at;
          params.before_id = cursor.id;
        }
        const response = await api.get("/api/activities/page", { params });
        const page = normalizeActivityPageResponse(response.data);
        setActivities((prev) => (append ? mergeActivities(prev, page.items) : page.items));
        setActivitiesHasMore(page.hasMore);
        setActivitiesCursor(page.nextCursor);
      } catch (error) {
        toast.error(extractApiError(error, "Aktivitäten konnten nicht geladen werden."));
        if (!append) {
          setActivities([]);
          setActivitiesHasMore(false);
          setActivitiesCursor(null);
        }
      } finally {
        if (append) setActivitiesLoadingMore(false);
      }
    },
    [id]
  );

  const handleDeleteActivity = useCallback(async (activity) => {
    try {
      await api.delete(`/api/activities/${activity.id}`);
      setActivities((prev) => prev.filter((item) => item.id !== activity.id));
      toast.success("Aktivität gelöscht.");
    } catch (error) {
      const msg = extractApiError(error, "Aktivität konnte nicht gelöscht werden.");
      toast.error(msg);
      throw error;
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    setLoadError("");
    setCompany(null);
    setContact(null);

    try {
      const dealResponse = await api.get(`/api/deals/${id}`);
      const dealData = dealResponse.data;
      setDeal(dealData);

      await Promise.all([
        loadTasks(),
        loadActivities(),
        loadAssignees(),
        dealData.company_id
          ? api.get(`/api/companies/${dealData.company_id}`).then((response) => setCompany(response.data)).catch(() => setCompany(null))
          : Promise.resolve(setCompany(null)),
        dealData.contact_id
          ? api.get(`/api/contacts/${dealData.contact_id}`).then((response) => setContact(response.data)).catch(() => setContact(null))
          : Promise.resolve(setContact(null)),
      ]);
    } catch (error) {
      setDeal(null);
      setTasks([]);
      setActivities([]);
      setActivitiesHasMore(false);
      setActivitiesCursor(null);
      if (error?.response?.status === 404) {
        setNotFound(true);
        setLoadError("Der Deal wurde nicht gefunden.");
      } else {
        const message = extractApiError(error, "Deal konnte nicht geladen werden.");
        setLoadError(message);
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  }, [id, loadActivities, loadAssignees, loadTasks]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateTask = async (event) => {
    event.preventDefault();
    if (!canEdit) return;
    if (!taskForm.title.trim()) {
      toast.error("Bitte einen Titel angeben.");
      return;
    }
    setTaskSaving(true);
    try {
      const assignedUserId = toAssignedUserId(taskForm.assigned_user_id);
      const payload = {
        title: taskForm.title,
        description: taskForm.description,
        due_date: taskForm.due_date ? new Date(taskForm.due_date).toISOString() : null,
        priority: taskForm.priority,
        status: taskForm.status,
        related_deal_id: id,
        ...(assignedUserId ? { assigned_user_id: assignedUserId } : {}),
      };
      await api.post("/api/tasks", payload);
      toast.success("Aufgabe erstellt.");
      setTaskForm(TASK_FORM_INITIAL);
      setTaskDialogOpen(false);
      loadTasks();
    } catch (error) {
      toast.error(extractApiError(error, "Aufgabe konnte nicht erstellt werden."));
    } finally {
      setTaskSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground py-12 text-center">Wird geladen …</div>;
  }

  if (notFound) {
    return (
      <div>
        <button
          type="button"
          onClick={() => navigate("/pipeline")}
          className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center mb-3"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Zurück zur Pipeline
        </button>
        <Card>
          <CardHeader>
            <CardTitle>Deal nicht gefunden</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-muted-foreground">{loadError || "Der Deal wurde nicht gefunden."}</div>
            <Button variant="outline" onClick={() => navigate("/pipeline")}>
              Zur Pipeline
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!deal) {
    return (
      <div>
        <button
          type="button"
          onClick={() => navigate("/pipeline")}
          className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center mb-3"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Zurück zur Pipeline
        </button>
        <Card>
          <CardHeader>
            <CardTitle>Deal konnte nicht geladen werden</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-sm text-muted-foreground">{loadError || "Bitte erneut versuchen."}</div>
            <Button variant="outline" onClick={load}>
              Erneut laden
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const stageMeta = getDealStageMeta(deal.stage);
  const assignedUserLabel = deal.assigned_user_id
    ? getTaskAssigneeLabel({ assignedUserId: deal.assigned_user_id, usersById: assigneesById }) || "Unbekannt"
    : "Nicht gesetzt";
  const isArchived = Boolean(deal.archived_at);
  const canEdit = canCreateOrEditCRM(perm) && !isArchived;
  const canArchive = canArchiveCRM(perm) && !isArchived;
  const canRestore = canRestoreCRM(perm) && isArchived;

  const handleArchive = async () => {
    if (!canArchive) return;
    setArchiving(true);
    try {
      await api.post(`/api/deals/${id}/archive`);
      toast.success("Deal wurde archiviert.");
      setArchiveOpen(false);
      load();
    } catch (error) {
      toast.error(extractApiError(error, "Archivieren fehlgeschlagen."));
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async () => {
    if (!canRestore) return;
    setRestoring(true);
    try {
      await api.post(`/api/deals/${id}/restore`);
      toast.success("Deal wurde wiederhergestellt.");
      load();
    } catch (error) {
      toast.error(extractApiError(error, "Wiederherstellen fehlgeschlagen."));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate("/pipeline")}
        className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center mb-3"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Zurück zur Pipeline
      </button>

      {isArchived ? (
        <Alert className="mb-4 border-border/60 bg-muted/20">
          <AlertTitle>Archiviert</AlertTitle>
          <AlertDescription>Dieser Deal ist archiviert.</AlertDescription>
        </Alert>
      ) : null}

      <PageHeader
        title={deal.title}
        description={
          <span className="inline-flex items-center gap-2">
            <StatusPill tone={stageMeta.tone}>{stageMeta.label}</StatusPill>
            <span className="text-xs text-muted-foreground">Erstellt am {formatDate(deal.created_at)}</span>
          </span>
        }
        actions={
          <>
            {canArchive ? (
              <Button type="button" variant="outline" onClick={() => setArchiveOpen(true)}>
                <Archive className="w-4 h-4 mr-1.5" /> Deal archivieren
              </Button>
            ) : null}
            {canRestore ? (
              <Button type="button" variant="outline" onClick={handleRestore} disabled={restoring}>
                <RotateCcw className="w-4 h-4 mr-1.5" /> Wiederherstellen
              </Button>
            ) : null}
            {canEdit ? (
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(true)}>
                <Pencil className="w-4 h-4" /> Deal bearbeiten
              </Button>
            ) : null}
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-7 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Deal-Details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Titel">
                <ReadOnlyValue>{deal.title || "Nicht gesetzt"}</ReadOnlyValue>
              </Field>
              <Field label="Phase">
                <ReadOnlyValue>{stageMeta.label}</ReadOnlyValue>
              </Field>
              <Field label="Wert">
                <ReadOnlyValue>{deal.value == null ? "Nicht gesetzt" : formatMoney(deal.value)}</ReadOnlyValue>
              </Field>
              <Field label="Abrechnungsmodell">
                <ReadOnlyValue>{billingModelLabel(deal.billing_model)}</ReadOnlyValue>
              </Field>
              <Field label="Einmalumsatz">
                <ReadOnlyValue>
                  {deal.one_time_amount == null ? "Nicht gesetzt" : formatMoney(deal.one_time_amount)}
                </ReadOnlyValue>
              </Field>
              <Field label="Wiederkehrender Umsatz">
                <ReadOnlyValue>
                  {deal.recurring_amount == null ? "Nicht gesetzt" : formatMoney(deal.recurring_amount)}
                </ReadOnlyValue>
              </Field>
              <Field label="Intervall">
                <ReadOnlyValue>{deal.recurring_interval || "Nicht gesetzt"}</ReadOnlyValue>
              </Field>
              <Field label="Laufzeit">
                <ReadOnlyValue>
                  {deal.recurring_term_months == null ? "Offen / 12 Monate Annahme" : `${deal.recurring_term_months} Monate`}
                </ReadOnlyValue>
              </Field>
              <Field label="Währung">
                <ReadOnlyValue>{deal.currency || "EUR"}</ReadOnlyValue>
              </Field>
              <Field label="Wahrscheinlichkeit">
                <ReadOnlyValue>{deal.probability == null ? "Nicht gesetzt" : `${deal.probability} %`}</ReadOnlyValue>
              </Field>
              <Field label="MRR">
                <ReadOnlyValue>{deal.mrr == null ? "Nicht gesetzt" : formatMoney(deal.mrr)}</ReadOnlyValue>
              </Field>
              <Field label="ARR">
                <ReadOnlyValue>{deal.arr == null ? "Nicht gesetzt" : formatMoney(deal.arr)}</ReadOnlyValue>
              </Field>
              <Field label="TCV">
                <ReadOnlyValue>{deal.tcv == null ? "Nicht gesetzt" : formatMoney(deal.tcv)}</ReadOnlyValue>
              </Field>
              <Field label="Gewichteter Forecast">
                <ReadOnlyValue>
                  {deal.weighted_tcv == null ? "Nicht gesetzt" : formatMoney(deal.weighted_tcv)}
                </ReadOnlyValue>
              </Field>
              <Field label="Erwarteter Abschluss">
                <ReadOnlyValue>
                  {deal.expected_close_date ? formatDate(deal.expected_close_date) : "Nicht gesetzt"}
                </ReadOnlyValue>
              </Field>
              <Field label="Zuständig">
                <ReadOnlyValue>{assignedUserLabel}</ReadOnlyValue>
              </Field>
              <Field label="Zugeordnete Firma">
                {deal.company_id ? (
                  <RelationButton
                    label={company?.name || "Firma öffnen"}
                    onClick={() => navigate(`/firmen/${deal.company_id}`)}
                  />
                ) : (
                  <ReadOnlyValue muted>Keine Firma verknüpft.</ReadOnlyValue>
                )}
              </Field>
              <Field label="Zugeordneter Kontakt">
                {deal.contact_id ? (
                  <div>
                    <RelationButton
                      label={contact ? `${contact.first_name} ${contact.last_name}` : "Kontakt öffnen"}
                      onClick={() => navigate(`/kontakte/${deal.contact_id}`)}
                    />
                  </div>
                ) : (
                  <ReadOnlyValue muted>Kein Kontakt verknüpft.</ReadOnlyValue>
                )}
              </Field>
              <div className="md:col-span-2">
                <Field label="Revenue">
                  <ReadOnlyText>
                    {(deal.billing_model || "one_time") === "hybrid"
                      ? "Hybrid"
                      : (deal.billing_model || "one_time") === "recurring"
                        ? "Wiederkehrend"
                        : "Einmalig"}
                  </ReadOnlyText>
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Notizen">
                  <ReadOnlyText>{deal.notes || "Nicht gesetzt"}</ReadOnlyText>
                </Field>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-5 space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Verknüpfte Aufgaben</CardTitle>
              {canEdit ? (
                <Button size="sm" variant="outline" onClick={() => setTaskDialogOpen(true)}>
                  <Plus className="w-4 h-4" /> Aufgabe hinzufügen
                </Button>
              ) : null}
            </CardHeader>
            <CardContent>
              {tasks.length === 0 ? (
                <div className="text-sm text-muted-foreground">Noch keine Aufgaben verknüpft.</div>
              ) : (
                <ul className="divide-y divide-border/60">
                  {tasks.map((task) => {
                    const statusMeta = TASK_STATUS_LABELS[task.status] || { de: task.status, tone: "muted" };
                    const priorityMeta = PRIORITY_LABELS[task.priority] || { de: task.priority, tone: "muted" };
                    const overdue = isTaskOverdue(task.due_date, task.status);
                    const assigneeLabel = task.assigned_user_id
                      ? getTaskAssigneeLabel({ assignedUserId: task.assigned_user_id, usersById: assigneesById })
                      : "";
                    return (
                      <li key={task.id} className="py-2 flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <button
                            type="button"
                            className="text-sm font-medium break-words hover:underline text-left"
                            onClick={() => navigate(`/aufgaben/${task.id}`)}
                          >
                            {task.title}
                          </button>
                          {task.due_date ? (
                            <div className={`text-xs ${overdue ? "text-destructive" : "text-muted-foreground"}`}>
                              Fällig {formatDate(task.due_date)}
                            </div>
                          ) : null}
                          {task.assigned_user_id ? (
                            <div className="text-xs text-muted-foreground">Zugewiesen an: {assigneeLabel}</div>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {overdue ? <StatusPill tone="destructive">Überfällig</StatusPill> : null}
                          <StatusPill tone={priorityMeta.tone}>{priorityMeta.de}</StatusPill>
                          <StatusPill tone={statusMeta.tone}>{statusMeta.de}</StatusPill>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Aktivitäten</CardTitle>
              {canEdit ? (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setQuickNoteDialogOpen(true)}>
                    <Pencil className="w-4 h-4 mr-2" /> Notiz
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setActivityDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Aktivität
                  </Button>
                </div>
              ) : null}
            </CardHeader>
            <CardContent>
              <Timeline
                items={activities}
                usersById={assigneesById}
                emptyText="Noch keine Aktivitäten."
                currentContext={{ type: "deal", id: deal.id }}
                canDelete={canDeleteActivities}
                onDelete={handleDeleteActivity}
              />
              <div className="mt-4 flex flex-col items-center gap-2">
                {activitiesHasMore ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => loadActivities({ cursor: activitiesCursor, append: true })}
                    disabled={activitiesLoadingMore}
                  >
                    {activitiesLoadingMore ? "Weitere Aktivitäten werden geladen …" : "Mehr Aktivitäten laden"}
                  </Button>
                ) : activities.length > 0 ? (
                  <div className="text-xs text-muted-foreground">Alle Aktivitäten geladen</div>
                ) : null}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog
        open={taskDialogOpen}
        onOpenChange={(open) => {
          if (taskSaving) return;
          setTaskDialogOpen(open);
          if (!open) setTaskForm(TASK_FORM_INITIAL);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Aufgabe hinzufügen</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-3" data-testid="deal-task-create-form">
            <Field label="Titel *">
              <Input
                value={taskForm.title}
                onChange={(event) => setTaskForm((state) => ({ ...state, title: event.target.value }))}
                autoFocus
                data-testid="deal-task-form-title"
              />
            </Field>
            <Field label="Beschreibung">
              <Textarea
                rows={3}
                value={taskForm.description}
                onChange={(event) => setTaskForm((state) => ({ ...state, description: event.target.value }))}
              />
            </Field>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Fälligkeitsdatum">
                <Input
                  type="date"
                  value={taskForm.due_date}
                  onChange={(event) => setTaskForm((state) => ({ ...state, due_date: event.target.value }))}
                />
              </Field>
              <Field label="Zugewiesen an">
                <Select
                  value={taskForm.assigned_user_id}
                  onValueChange={(value) => setTaskForm((state) => ({ ...state, assigned_user_id: value }))}
                  disabled={assigneesLoading || Boolean(assigneesError)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ASSIGNEE_UNASSIGNED}>Nicht zugewiesen</SelectItem>
                    {assigneeOptions.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {assigneesError ? <div className="text-xs text-muted-foreground mt-1">{assigneesError}</div> : null}
              </Field>
              <Field label="Priorität">
                <Select
                  value={taskForm.priority}
                  onValueChange={(value) => setTaskForm((state) => ({ ...state, priority: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(PRIORITY).map((priority) => (
                      <SelectItem key={priority} value={priority}>
                        {PRIORITY_LABELS[priority].de}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select
                  value={taskForm.status}
                  onValueChange={(value) => setTaskForm((state) => ({ ...state, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.values(TASK_STATUS).map((status) => (
                      <SelectItem key={status} value={status}>
                        {TASK_STATUS_LABELS[status].de}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTaskDialogOpen(false)} disabled={taskSaving}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={taskSaving}>
                {taskSaving ? "Speichern …" : "Erstellen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <QuickNoteDialog
        open={quickNoteDialogOpen}
        onOpenChange={setQuickNoteDialogOpen}
        relationPayload={{ related_deal_id: id }}
        onCreated={() => loadActivities()}
      />
      <AddActivityDialog
        open={activityDialogOpen}
        onOpenChange={setActivityDialogOpen}
        relationPayload={{ related_deal_id: id }}
        onCreated={() => loadActivities()}
      />

      <AlertDialog
        open={archiveOpen}
        onOpenChange={(open) => {
          if (archiving) return;
          setArchiveOpen(open);
        }}
      >
        <AlertDialogContent className="border-border/60 shadow-lg shadow-black/10 outline-none ring-0 ring-offset-0">
          <AlertDialogHeader>
            <AlertDialogTitle>Deal archivieren?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchtest du diesen Deal wirklich archivieren? Der Deal wird aus der aktiven Pipeline
              ausgeblendet, kann aber später wiederhergestellt werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiving}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={archiving}
              onClick={(event) => {
                event.preventDefault();
                handleArchive();
              }}
            >
              {archiving ? "Archivieren ..." : "Archivieren"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DealFormDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        mode="edit"
        initialDeal={deal}
        dealId={deal.id}
        onSaved={() => {
          setEditDialogOpen(false);
          load();
        }}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function ReadOnlyValue({ children, muted = false }) {
  return <div className={`text-sm ${muted ? "text-muted-foreground" : "text-foreground"}`}>{children}</div>;
}

function ReadOnlyText({ children }) {
  return (
    <div className="min-h-[96px] rounded-md border border-border bg-muted/20 px-3 py-2 text-sm whitespace-pre-wrap">
      {children}
    </div>
  );
}

function RelationButton({ label, onClick }) {
  return (
    <button type="button" className="text-sm font-medium hover:underline text-left" onClick={onClick}>
      {label}
    </button>
  );
}

function getDealStageMeta(stage) {
  if (stage === "WON") return { label: DEAL_STAGE_LABELS[stage] || stage, tone: "success" };
  if (stage === "LOST") return { label: DEAL_STAGE_LABELS[stage] || stage, tone: "destructive" };
  if (stage === "OFFER_SENT" || stage === "FOLLOW_UP") {
    return { label: DEAL_STAGE_LABELS[stage] || stage, tone: "warning" };
  }
  return { label: DEAL_STAGE_LABELS[stage] || stage || "Nicht gesetzt", tone: "info" };
}
